import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, Pencil, GitBranch } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EDGE_KINDS, KIND_BADGE } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

export function EdgeDrawer({ edge, open, onOpenChange, workspaceId, nodeColumns, onChanged, canReview }) {
  const [sample, setSample] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ from_column: "", to_column: "", kind: "joins_on" });

  useEffect(() => {
    if (!open || !edge) return;
    setEditing(false);
    setForm({ from_column: edge.from_column || "", to_column: edge.to_column || "", kind: edge.kind || "joins_on" });
    setSample(null);
    setLoadingSample(true);
    api
      .get(`/semantics/joins/${edge.id}/sample`, { params: { workspace_id: workspaceId } })
      .then(({ data }) => setSample(data))
      .catch(() => setSample({ columns: [], rows: [], error: "Could not load sample rows." }))
      .finally(() => setLoadingSample(false));
  }, [open, edge, workspaceId]);

  if (!edge) return null;
  const fromCols = nodeColumns[edge.from_fqn] || [];
  const toCols = nodeColumns[edge.to_fqn] || [];

  const act = async (action, body = {}) => {
    setBusy(true);
    try {
      await api.patch(`/semantics/joins/${edge.id}`, { action, ...body });
      toast.success(
        action === "verify" ? "Join verified — now trusted for query planning"
          : action === "reject" ? "Join rejected"
          : "Join updated"
      );
      setEditing(false);
      onChanged?.();
      if (action !== "edit") onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid={SEMANTICS.edgeDrawer} className="w-full overflow-y-auto font-mono sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <GitBranch className="h-4 w-4 text-accent" /> Join relationship
          </SheetTitle>
          <SheetDescription className="text-xs">
            Verify this join to trust it for cross-table query planning.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          <div className="rounded-lg border border-border/60 bg-card/40 p-3 text-xs">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium">{edge.from_fqn}<span className="text-muted-foreground">.{edge.from_column}</span></span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium">{edge.to_fqn}<span className="text-muted-foreground">.{edge.to_column}</span></span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] ${KIND_BADGE[edge.kind] || ""}`}>{edge.kind}</Badge>
              <span className="text-[10px] text-muted-foreground">confidence {edge.confidence}</span>
              <span className={`rounded-full px-2 py-0 text-[10px] uppercase ${
                edge.status === "confirmed" ? "text-blue-400" : edge.status === "rejected" ? "text-red-400" : "text-muted-foreground"
              }`}>{edge.status}</span>
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Why this edge?</div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {edge.rationale || (edge.inferred
                ? `Inferred from a foreign-key naming match between ${edge.from_fqn}.${edge.from_column} and ${edge.to_fqn}.${edge.to_column} (scored ${edge.confidence}).`
                : "Manually added by a workspace member.")}
            </p>
            {edge.join_expr && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Join</span>
                <code className="rounded bg-muted/40 px-2 py-0.5 text-[11px] text-accent">{edge.join_expr}</code>
              </div>
            )}
            {edge.evidence && Object.keys(edge.evidence).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {edge.evidence.name_match && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">name matches table</span>}
                {edge.evidence.fk_column_in_target && <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] text-sky-400">key column in target</span>}
                {edge.evidence.target_has_id && <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">target has id PK</span>}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Sample rows (matching keys)</div>
            {loadingSample ? (
              <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
            ) : sample?.rows?.length ? (
              <div className="overflow-hidden rounded-lg border border-border/60">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/40 text-muted-foreground">
                    <tr>{sample.columns.map((c) => <th key={c} className="px-2 py-1 text-left font-normal">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {sample.rows.map((r, i) => (
                      <tr key={i} data-testid={SEMANTICS.edgeSampleRow} className="border-t border-border/40">
                        {sample.columns.map((c) => <td key={c} className="px-2 py-1">{String(r[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="py-2 text-xs text-muted-foreground">{sample?.error || "No matching rows to demonstrate this join."}</p>
            )}
          </div>

          {editing && (
            <div className="space-y-3 rounded-lg border border-accent/40 bg-accent/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Edit join</div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.from_column} onValueChange={(v) => setForm((f) => ({ ...f, from_column: v }))}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="From column" /></SelectTrigger>
                  <SelectContent>{fromCols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.to_column} onValueChange={(v) => setForm((f) => ({ ...f, to_column: v }))}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="To column" /></SelectTrigger>
                  <SelectContent>{toCols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{EDGE_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
              <Button
                size="sm"
                data-testid={SEMANTICS.edgeEditSave}
                disabled={busy}
                onClick={() => act("edit", form)}
                className="w-full rounded-md bg-foreground text-xs uppercase tracking-[0.14em] text-background hover:bg-foreground/90"
              >
                Save changes
              </Button>
            </div>
          )}

          {canReview && !editing && (
            <div className="flex items-center gap-2">
              <Button
                size="sm" data-testid={SEMANTICS.edgeVerify} disabled={busy || edge.status === "confirmed"}
                onClick={() => act("verify")}
                className="flex-1 rounded-md bg-blue-600 text-xs uppercase tracking-[0.14em] text-white hover:bg-blue-600/90"
              >
                <Check className="mr-1 h-3.5 w-3.5" /> Verify
              </Button>
              <Button
                size="sm" variant="outline" data-testid={SEMANTICS.edgeReject} disabled={busy || edge.status === "rejected"}
                onClick={() => act("reject")}
                className="flex-1 rounded-md border-red-500/40 text-xs uppercase tracking-[0.14em] text-red-400 hover:bg-red-500/10"
              >
                <X className="mr-1 h-3.5 w-3.5" /> Reject
              </Button>
              <Button
                size="sm" variant="outline" data-testid={SEMANTICS.edgeEdit} disabled={busy}
                onClick={() => setEditing(true)}
                className="rounded-md border-border/70 text-xs uppercase tracking-[0.14em]"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {!canReview && (
            <p className="text-[11px] text-muted-foreground">Viewer role — analyst+ can verify, reject or edit joins.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
